/**
 * FormValidator Class
 * Handles client-side form validation with real-time feedback
 */
class FormValidator {
    constructor(form) {
        this.form = form;
        this.fields = {};
        this.submitButton = this.form.querySelector('input[type="submit"], button[type="submit"]');
        this.attemptedSubmit = false;
        this.isValidating = false; // Flag to prevent multiple simultaneous validations
        this.errorsShown = false; // Flag to prevent showing errors multiple times
        this.shownErrors = new Set(); // Track which fields have had errors shown
    }

    init() {
        this.setupFields();
        this.bindEvents();
        this.setupRealTimeValidation();
        
        // Remove any existing onsubmit handlers and set our own
        this.form.onsubmit = null;
        
        // Set initial button state
        this.updateSubmitButtonState();
    }

    setupFields() {
        // Get all form fields
        const inputs = this.form.querySelectorAll('input, select, textarea');
        
        inputs.forEach(input => {
            const fieldName = input.name || input.id;
            
            // Skip submit buttons and other non-validation fields
            if (fieldName && input.type !== 'submit') {
                // Skip checkbox inputs that are part of custom UI (they're just for display)
                if (input.type === 'checkbox' && fieldName.startsWith('region-')) {
                    return;
                }
                
                // Include hidden fields that have data-key="user_regions"
                if (input.type === 'hidden' && input.getAttribute('data-key') === 'user_regions') {
                    // Include this hidden field for validation
                } else if (input.type === 'hidden') {
                    return;
                }
                
                // Skip export/erase data fields - they should not be validated
                if (fieldName.includes('um-export-data') || fieldName.includes('um-erase-data')) {
                    return;
                }
                
                const rules = this.getFieldRules(input);
                this.fields[fieldName] = {
                    element: input,
                    rules: rules,
                    isValid: true,
                    errorMessage: ''
                };
            }
        });
    }

    getFieldRules(input) {
        const rules = {};
        const fieldName = input.name || input.id;

        // Basic required validation
        if (input.hasAttribute('required') || input.classList.contains('required')) {
            rules.required = true;
        }

        // Field-specific rules - check for field name patterns
        if (fieldName.includes('confirm_user_email')) {
            rules.required = true;
            rules.confirmEmail = true; // Special validation to match user_email
        } else if (fieldName.includes('user_email') || fieldName.includes('email') || fieldName.includes('username_b')) {
            rules.required = true;
            rules.email = true;
        } else if (fieldName.includes('user_password') && !fieldName.includes('current_user_password') && !fieldName.includes('single_user_password') && !fieldName.includes('confirm_user_password')) {
            rules.required = true;
            // Check if this is a registration form by looking for registration-specific fields
            const hasRegistrationFields = this.form.querySelector('[name*="first_name"], [name*="last_name"], [name*="company_name"], [name*="registration_code"]');
            // Check if this is an account form by looking for account-specific fields or form class
            const isAccountForm = this.form.closest('.um-account') || this.form.querySelector('[name*="current_user_password"]');
            
            if (hasRegistrationFields && !isAccountForm) {
                // Registration form: strong password requirements
                rules.minLength = 8;
                rules.maxLength = 64;
                rules.strongPassword = true;
            } else if (isAccountForm) {
                // Account form: strong password requirements for new passwords
                rules.minLength = 8;
                rules.maxLength = 64;
                rules.strongPassword = true;
            } else {
                // Login form: just check it's not empty
                rules.minLength = 1;
            }
        } else if (fieldName.includes('confirm_user_password') || fieldName.includes('confirm_password')) {
            rules.required = true;
            rules.confirmPassword = true;
        } else if (fieldName.includes('current_user_password')) {
            rules.required = true;
            rules.minLength = 1;
        } else if (fieldName.includes('single_user_password')) {
            rules.required = true;
            rules.minLength = 1;
        } else if (fieldName.includes('first_name')) {
            rules.required = true;
        } else if (fieldName.includes('last_name')) {
            rules.required = true;
        } else if (fieldName.includes('company_name')) {
            rules.required = true;
        } else if (fieldName.includes('registration_code')) {
            rules.required = true;
        } else if (fieldName.includes('username') || fieldName.includes('user_login')) {
            rules.required = true;
            rules.minLength = 3;
        } else if (fieldName.includes('user_regions')) {
            rules.required = true;
            rules.regions = true; // Special validation for regions field
        } else if (fieldName.includes('region')) {
            rules.required = true;
            rules.select = true; // Special validation for select fields
        }

        // Also check by input type
        if (input.type === 'email') {
            rules.required = true;
            rules.email = true;
        }

        // Custom validation attributes
        if (input.hasAttribute('data-min-length')) {
            rules.minLength = parseInt(input.getAttribute('data-min-length'));
        }
        if (input.hasAttribute('data-max-length')) {
            rules.maxLength = parseInt(input.getAttribute('data-max-length'));
        }
        if (input.hasAttribute('data-pattern')) {
            rules.pattern = input.getAttribute('data-pattern');
        }

        return rules;
    }

    bindEvents() {
        // Only validate on submit button click (not form submit event)
        if (this.submitButton) {
            this.submitButton.addEventListener('click', (e) => {
                this.attemptedSubmit = true;
                const formValid = this.validateForm();
                if (!formValid) {
                    e.preventDefault();
                    e.stopPropagation();
                    this.showFormErrors();
                    this.updateSubmitButtonState();
                    return false;
                }
            }, true);
        }

        // Validation on blur (when user leaves field)
        Object.values(this.fields).forEach(field => {
            field.touched = false;
            field.element.addEventListener('blur', () => {
                field.touched = true;
                this.validateField(field);
                this.updateSubmitButtonState();
            });

            // Clear errors on input (but don't validate immediately)
            field.element.addEventListener('input', () => {
                if (!field.touched) field.touched = true;
                this.clearFieldError(field);
                this.updateSubmitButtonState();
            });

            // For select fields, also validate on change
            if (field.element.tagName === 'SELECT') {
                field.element.addEventListener('change', () => {
                    field.touched = true;
                    this.validateField(field);
                    this.updateSubmitButtonState();
                });
            }
        });
    }

    setupRealTimeValidation() {
        // Add debounced validation for real-time feedback
        Object.values(this.fields).forEach(field => {
            let debounceTimer;
            
            // Standard input event listener for most fields
            field.element.addEventListener('input', () => {
                // Clear any existing timer
                clearTimeout(debounceTimer);
                
                // Only validate if there's content and after user stops typing
                if (field.element.value.length > 0) {
                    debounceTimer = setTimeout(() => {
                        this.validateField(field);
                    }, 1000); // Wait 1 second after user stops typing
                }
            });

            // Special handling for the specific user_regions field on registration page that is updated programmatically
            if (field.element.id === 'user_regions-481') {
                // Use MutationObserver to watch for value attribute changes
                const observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
                            // Clear any existing timer
                            clearTimeout(debounceTimer);
                            
                            // Validate immediately for programmatic changes
                            debounceTimer = setTimeout(() => {
                                // Mark field as touched since it was programmatically updated
                                field.touched = true;
                                this.validateField(field);
                            }, 100); // Shorter delay for programmatic changes
                        }
                    });
                });

                // Start observing the field for attribute changes
                observer.observe(field.element, {
                    attributes: true,
                    attributeFilter: ['value']
                });
            }
        });
    }

    validateField(field) {
        const value = field.element.value.trim();
        const rules = field.rules;
        let isValid = true;
        let errorMessage = '';

        // Only validate if field is touched or form submit attempted
        if (!field.touched && !this.attemptedSubmit) {
            field.isValid = true;
            field.errorMessage = '';
            return true;
        }

        // Required validation
        if (rules.required && !value && !field.element.name.includes('user_regions')) {
            isValid = false;
            errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'required');
        }

        // Min length validation
        if (isValid && rules.minLength && value.length < rules.minLength) {
            isValid = false;
            errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'minLength', rules.minLength);
        }

        // Max length validation
        if (isValid && rules.maxLength && value.length > rules.maxLength) {
            isValid = false;
            errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'maxLength', rules.maxLength);
        }

        // Email validation
        if (isValid && rules.email && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(value)) {
                isValid = false;
                errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'email');
            }
        }

        // Select validation (for dropdown/select fields)
        if (isValid && rules.select && field.element.tagName === 'SELECT') {
            // For region fields, check if value is empty or the placeholder option
            if (field.element.name && field.element.name.includes('region')) {
                if (!value || value === '' || value === 'Select Region') {
                    isValid = false;
                    errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'select');
                }
            } else {
                // For other select fields, use the original logic
                if (!value || value === '' || field.element.selectedIndex === 0) {
                    isValid = false;
                    errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'select');
                }
            }
        }

        // Regions validation (for custom regions field)
        if (isValid && rules.regions) {
            // For the specific user_regions field on registration page, check if the JSON array is empty or contains no valid selections
            if (field.element.id === 'user_regions-481') {
                try {
                    const selectedValues = JSON.parse(value || '[]');
                    if (!selectedValues || selectedValues.length === 0) {
                        isValid = false;
                        errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'regions');
                    }
                } catch (e) {
                    // If JSON parsing fails, consider it invalid
                    isValid = false;
                    errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'regions');
                }
            }
        }

        // Pattern validation
        if (isValid && rules.pattern && value) {
            const pattern = new RegExp(rules.pattern);
            if (!pattern.test(value)) {
                isValid = false;
                errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'pattern');
            }
        }

        // Strong password validation (for registration)
        if (isValid && rules.strongPassword && value) {
            const hasUpperCase = /[A-Z]/.test(value);
            const hasLowerCase = /[a-z]/.test(value);
            const hasNumbers = /\d/.test(value);
            
            if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
                isValid = false;
                errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'strongPassword');
            }
        }

        // Confirm password validation
        if (isValid && rules.confirmPassword && value) {
            // Find the actual password input field (not wrapper divs)
            const passwordField = this.form.querySelector('input[name*="user_password"]:not([name*="confirm_user_password"]):not([name*="current_user_password"]):not([name*="single_user_password"]), input[id*="user_password"]:not([id*="confirm_user_password"]):not([id*="current_user_password"]):not([id*="single_user_password"])');
            
            if (passwordField && value !== passwordField.value) {
                isValid = false;
                errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'confirmPassword');
            }
        }

        // Confirm email validation
        if (isValid && rules.confirmEmail && value) {
            // Find the actual email input field
            const emailField = this.form.querySelector('input[name="user_email"], input[id*="user_email"]');
            
            if (emailField && value !== emailField.value) {
                isValid = false;
                errorMessage = this.getErrorMessage(field.element.name || field.element.id, 'confirmEmail');
            }
        }

        // Update field state
        field.isValid = isValid;
        field.errorMessage = errorMessage;

        // Show/hide error
        if (!isValid && (field.touched || this.attemptedSubmit)) {
            this.showFieldError(field);
        } else if (isValid) {
            this.clearFieldError(field);
            // Add success state for valid fields
            if (field.touched || this.attemptedSubmit) {
                this.showFieldSuccess(field);
            }
        }

        return isValid;
    }

    validateForm() {
        if (this.isValidating) {
            return false; // Prevent multiple simultaneous validations
        }
        
        this.isValidating = true;
        let isValid = true;
        
        Object.values(this.fields).forEach(field => {
            if (!this.validateField(field)) {
                isValid = false;
            }
        });

        this.isValidating = false;
        return isValid;
    }

    getErrorMessage(fieldName, type, param = null) {
        // Normalize field name by removing suffixes
        const normalizedFieldName = fieldName.replace(/-\d+$/, ''); // Remove -482, -123, etc.
        
        const messages = {
            username: {
                required: 'Please enter your username or email',
                minLength: `Username must be at least ${param} characters long`
            },
            user_login: {
                required: 'Please enter your username or email',
                minLength: `Username must be at least ${param} characters long`
            },
            user_password: {
                required: 'Please enter your password',
                minLength: `Password must be at least ${param} characters long`,
                strongPassword: 'Password must contain at least 1 uppercase letter, 1 lowercase letter, and 1 number'
            },
            confirm_user_password: {
                required: 'Please confirm your password',
                confirmPassword: 'Passwords do not match'
            },
            current_user_password: {
                required: 'Please enter your current password',
                minLength: 'Please enter your current password'
            },
            single_user_password: {
                required: 'Please enter your password',
                minLength: 'Please enter your password'
            },
            user_email: {
                required: 'Please enter your email address',
                email: 'Valid email required'
            },
            confirm_user_email: {
                required: 'Please confirm your email address',
                confirmEmail: 'Email addresses do not match'
            },
            email: {
                required: 'Please enter your email address',
                email: 'Please enter a valid email address'
            },
            username_b: {
                required: 'Please enter your email address',
                email: 'Please enter a valid email address'
            },
            first_name: {
                required: 'Please enter your first name'
            },
            last_name: {
                required: 'Please enter your last name'
            },
            company_name: {
                required: 'Please enter your company name'
            },
            registration_code: {
                required: 'Please enter a registration code'
            },
            region: {
                required: 'Please select a region',
                select: 'Please select a region'
            },
            user_regions: {
                required: 'Please select at least one region',
                regions: 'Please select at least one region'
            }
        };

        // Try to get message for normalized field name first
        let fieldMessages = messages[normalizedFieldName] || {};
        
        // If no message found, try the original field name
        if (!fieldMessages[type]) {
            fieldMessages = messages[fieldName] || {};
        }
        
        return fieldMessages[type] || 'This field is invalid';
    }

    showFieldError(field) {
        // Check if error has already been shown for this field
        if (this.shownErrors.has(field.element.name)) {
            return;
        }
        
        this.clearFieldError(field);
        
        // Add error class to field
        field.element.classList.add('um-error');
        field.element.classList.add('form-error');

        // Create error message element
        const errorElement = document.createElement('div');
        errorElement.className = 'um-field-error form-error-message';
        errorElement.textContent = field.errorMessage;
        errorElement.setAttribute('role', 'alert');

        // For the specific user_regions field on registration page, place error as last child of the region-selector element
        if (field.element.id === 'user_regions-481') {
            // Find the custom regions field from the form level since the hidden field is separate
            const customRegionsField = this.form.querySelector('.um-field-multiselect');
            
            if (customRegionsField) {
                // Check if there's already an error message inside this container
                const existingError = customRegionsField.querySelector('.um-field-error');
                if (existingError) {
                    existingError.remove();
                }
                
                // Place error as last child of the region-selector element
                const regionSelector = customRegionsField.querySelector('.region-selector');
                if (regionSelector) {
                    regionSelector.appendChild(errorElement);
                } else {
                    // Fallback to original method
                    const parentNode = field.element.parentNode;
                    parentNode.insertBefore(errorElement, field.element.nextSibling);
                }
            } else {
                // Fallback to original method
                const parentNode = field.element.parentNode;
                parentNode.insertBefore(errorElement, field.element.nextSibling);
            }
        } else if (field.element.name && field.element.name.includes('region')) {
            const regionContainer = field.element.closest('.region-selector');
            if (regionContainer) {
                // Check if there's already an error message inside this container
                const existingError = regionContainer.querySelector('.um-field-error');
                if (existingError) {
                    existingError.remove();
                }
                regionContainer.appendChild(errorElement);
            } else {
                // Fallback to original method
                const parentNode = field.element.parentNode;
                parentNode.insertBefore(errorElement, field.element.nextSibling);
            }
        } else {
            // Insert error message after the field
            const parentNode = field.element.parentNode;
            parentNode.insertBefore(errorElement, field.element.nextSibling);
        }

        // Add aria attributes
        field.element.setAttribute('aria-invalid', 'true');
        field.element.setAttribute('aria-describedby', errorElement.id || 'error-' + Math.random());
        
        // Mark this error as shown
        this.shownErrors.add(field.element.name);
        
        // Update submit button state
        this.updateSubmitButtonState();
    }

    showFieldSuccess(field) {
        // Remove error classes
        field.element.classList.remove('um-error', 'form-error');

        // Add success class
        field.element.classList.add('um-success');

        // Remove aria attributes
        field.element.removeAttribute('aria-invalid');
        field.element.removeAttribute('aria-describedby');
    }

    clearFieldError(field) {
        // Remove error classes
        field.element.classList.remove('um-error', 'form-error');
        
        // Remove from shown errors set
        this.shownErrors.delete(field.element.name);

        // For the specific user_regions field on registration page, remove error message from the region-selector container
        if (field.element.id === 'user_regions-481') {
            const regionSelector = this.form.querySelector('.region-selector');
            if (regionSelector) {
                const existingError = regionSelector.querySelector('.um-field-error');
                if (existingError) {
                    existingError.remove();
                }
            }
        } else if (field.element.name && field.element.name.includes('region')) {
            const regionContainer = field.element.closest('.region-selector');
            if (regionContainer) {
                const existingError = regionContainer.querySelector('.um-field-error');
                if (existingError) {
                    existingError.remove();
                }
            }
        } else {
            // Remove error message element - only if it's directly after this field
            const existingError = field.element.nextSibling;
            if (existingError && existingError.classList && existingError.classList.contains('um-field-error')) {
                existingError.remove();
            }
        }

        // Remove aria attributes
        field.element.removeAttribute('aria-invalid');
        field.element.removeAttribute('aria-describedby');
        
        // Check if all fields are valid and update submit button
        this.updateSubmitButtonState();
    }

    updateSubmitButtonState() {
        if (!this.submitButton) {
            return;
        }
        // Only disable if user has tried to submit or interacted
        const hasErrors = Object.values(this.fields).some(field => !field.isValid && (field.touched || this.attemptedSubmit));
        if (hasErrors && (this.attemptedSubmit || Object.values(this.fields).some(f => f.touched))) {
            this.submitButton.disabled = true;
            this.submitButton.classList.add('disabled');
        } else {
            this.submitButton.disabled = false;
            this.submitButton.classList.remove('disabled');
        }
    }

    showFormErrors() {
        if (this.errorsShown) {
            return; // Don't show errors multiple times
        }
        
        // Mark all fields as touched and show errors for invalid fields
        Object.values(this.fields).forEach(field => {
            field.touched = true;
            // Show error for fields that are already invalid (no re-validation)
            if (!field.isValid) {
                this.showFieldError(field);
            }
        });

        // Focus on first error field
        const firstErrorField = Object.values(this.fields).find(field => !field.isValid);
        if (firstErrorField) {
            firstErrorField.element.focus();
        }
        
        this.errorsShown = true;
        
        // Reset the flag after a short delay
        setTimeout(() => {
            this.errorsShown = false;
        }, 100);
    }

    showGeneralError(message) {
        // Remove existing general error
        const existingError = this.form.querySelector('.form-general-error');
        if (existingError) {
            existingError.remove();
        }

        // Create general error message
        const errorElement = document.createElement('div');
        errorElement.className = 'um-notice err form-general-error';
        errorElement.innerHTML = `<i class="um-icon-ios-close-empty" onclick="this.parentElement.remove();"></i>${message}`;
        errorElement.setAttribute('role', 'alert');

        // Insert at the top of the form
        this.form.insertBefore(errorElement, this.form.firstChild);
    }

    // Public method to validate specific field
    validateSpecificField(fieldName) {
        const field = this.fields[fieldName];
        if (field) {
            return this.validateField(field);
        }
        return false;
    }

    // Public method to clear all errors
    clearAllErrors() {
        Object.values(this.fields).forEach(field => {
            this.clearFieldError(field);
        });

        const generalError = this.form.querySelector('.form-general-error');
        if (generalError) {
            generalError.remove();
        }
    }

    // Public method to get form validation status
    getFormStatus() {
        return {
            isValid: this.validateForm(),
            errors: Object.values(this.fields).filter(field => !field.isValid).map(field => ({
                field: field.element.name || field.element.id,
                message: field.errorMessage
            }))
        };
    }

    // Public method to validate a specific field by name (useful for Choices.js integration)
    validateFieldByName(fieldName) {
        const field = this.fields[fieldName];
        if (field) {
            field.touched = true;
            return this.validateField(field);
        }
        return false;
    }

    // Public method to get field by name
    getFieldByName(fieldName) {
        return this.fields[fieldName];
    }
}

// Export for use in other files
if (typeof module !== 'undefined' && module.exports) {
    module.exports = FormValidator;
} else if (typeof window !== 'undefined') {
    window.FormValidator = FormValidator;
}

// ES6 module export
export { FormValidator }; 